import { Container, HeaderContentContainer, ContentContainer } from './styles';
import React, { useRef } from 'react';
import { Route, Routes } from 'react-router-dom';
import { usePassedTime } from './hooks';
import { useResourcesPage } from './hooks/use-resources-page.hook';
import { useValidTypes } from './hooks/use-valid-types.hook';
import { useOnPopState } from './hooks/use-on-pop-state.hook';
import { Header } from './components/header';
import { useWebTracking } from './hooks/use-web-tracking.hook';
import { ArrowBackButton } from './components/ arrow-back-button';
import { LocationPicker } from 'pages/resources/components/location-picker';
import { FilterPicker } from './components/filter-picker';
import { TermsAndConditions } from 'components/termsAndConditions';
import { ModalContainer } from 'pages/resource/components/terms-and-conditions/components/modal-container';
import { ResourcesList } from './components/resources-list';
import { ResourcesSwipeModal } from './components/resources-swipe-modal';
import { MapViewContainer } from './components/map-view/map-view-container';
import { FilterSection } from 'hocs/filter-section';
import { MobileTitle } from 'pages/resources/components/mobile-title';
import { FloorChips, FloorplanView, ResourcesViewToggle } from 'floorplan/pages/native-floorplan/components';
import { ViewToggleRow } from 'floorplan/pages/native-floorplan/components/styles';
import { useResourcesView } from 'floorplan/pages/native-floorplan/hooks/use-resources-view.hook';

export const ResourcesPage = (): JSX.Element => {
  usePassedTime();
  useValidTypes();
  useOnPopState();
  useWebTracking();
  const {
    isLoadingState,
    shouldShowMapView,
    shouldShowResourcesSwipeModal,
    isMobileDevice,
    shouldShowFloorPlanFooter,
  } = useResourcesPage();
  const { isToggleVisible, isFloorplanView, floorplans, activeFloorplan, onFloorplanSelect, showList, showFloorplan } =
    useResourcesView();

  // Rendered either on its own or inside the toggle row, so it is built once here.
  const myBookings = isMobileDevice ? <MobileTitle /> : null;
  // `isLoadingState` goes up on *every* resource refetch, not just the first — including the one the
  // floorplan toggle itself triggers by writing a default date into the url (see
  // `useDefaultFloorplanDate`), and any later date/filter change made while the map is already
  // showing. Gating the toggle/map on it directly means both vanish behind the list's skeleton for
  // the duration of that refetch and reappear once it resolves — the "disappears and flashes" bug
  // (RES-1223). Only the very first load has nothing to show yet, so only that one should hide the
  // controls; the floorplan surface has its own in-place skeleton (`FloorplanMapSkeleton`, driven by
  // its own query) for every load after that.
  const hasRenderedContentRef = useRef(false);
  hasRenderedContentRef.current = hasRenderedContentRef.current || !isLoadingState;
  const isInitialLoading = isLoadingState && !hasRenderedContentRef.current;

  // Everything the floorplan feature adds — the toggle, the floor chips and the map — appears
  // together with the rest of the controls, so the page does not visibly rebuild itself.
  const showToggle = isToggleVisible && !isInitialLoading;
  const showFloorplanSurface = isFloorplanView && !isInitialLoading;

  return (
    <>
      <Header />
      <Container isLoading={isLoadingState}>
        <HeaderContentContainer>
          <ArrowBackButton />
          {/* The design puts the toggle on the same line as "My bookings", toggle on the left.
              On mobile the row is always rendered, even with the feature off: it holds "My
              bookings", and the flag itself arrives asynchronously — an empty `useFlags()` cannot be
              told apart from a disabled one, so waiting for it would move "My bookings" after the
              first frame. On desktop "My bookings" lives in the header instead, so an empty row
              would only reserve blank space; there it appears with the toggle. */}
          {isMobileDevice || showToggle ? (
            <ViewToggleRow>
              {showToggle && (
                <ResourcesViewToggle
                  isFloorplanView={isFloorplanView}
                  onShowList={showList}
                  onShowFloorplan={showFloorplan}
                />
              )}
              {myBookings}
            </ViewToggleRow>
          ) : null}
          <FilterSection>
            <LocationPicker />
            <FilterPicker />
          </FilterSection>
          {/* The floor chips belong with the controls, not with the map: that is where the design
              puts them, and it is what gives them the page's horizontal padding while the map
              stays full-bleed. */}
          {showFloorplanSurface && (
            <FloorChips
              floorplans={floorplans}
              activeFloorplanUuid={activeFloorplan?.uuid}
              onSelect={onFloorplanSelect}
            />
          )}
        </HeaderContentContainer>
        {/* Outside ContentContainer on purpose: the map has to fill the height the controls leave
            over, which needs a flex child of this column rather than a height:100% box. */}
        {showFloorplanSurface ? (
          <FloorplanView floorplan={activeFloorplan} />
        ) : (
          <ContentContainer>
            {!shouldShowResourcesSwipeModal && (
              <ResourcesList isLoadingState={isLoadingState} shouldShowFloorPlanFooter={shouldShowFloorPlanFooter} />
            )}
            {!isFloorplanView && shouldShowMapView && <MapViewContainer />}
          </ContentContainer>
        )}
        <Routes>
          <Route path="terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="quick-checkout/terms-and-conditions" element={<ModalContainer />} />
        </Routes>
        {!isFloorplanView && shouldShowResourcesSwipeModal && <ResourcesSwipeModal isLoadingState={isLoadingState} />}
      </Container>
    </>
  );
};

export default ResourcesPage;
